/**
 * The board's symbol ids — the wire's words, shared by the offline server and the presentation (which only reads them).
 *
 * | Id | What it is |
 * |---|---|
 * | `B` | blank (an empty cell) |
 * | `C:<minor>` | a coin worth `<minor>` minor units |
 * | `MINI` / `MINOR` / `MAJOR` | a jackpot coin |
 * | `MYS` | a mystery coin, revealed at the end of the bonus as a coin or a jackpot coin |
 * | `MJP` | a mystery jackpot, revealed at the end of the bonus as a jackpot coin: MINI, MINOR or MAJOR |
 * | `COL` / `COL:<minor>` | the collect coin, before / after it collects |
 */
export const BLANK = "B";
export const MYSTERY = "MYS";
export const MYSTERY_JACKPOT = "MJP";
export const COLLECT = "COL";

export type CoinJackpotTier = "mini" | "minor" | "major";
export type JackpotTier = CoinJackpotTier | "grand" | "royal";

/** Each jackpot in bet multiples: the coins' three, the full board's GRAND (12 cells) and ROYAL (20 cells, with GROW). */
export const JACKPOT_MULTIPLIERS: Readonly<Record<JackpotTier, number>> = { mini: 10, minor: 20, major: 50, grand: 500, royal: 2000 };

/** The jackpot coins' ids. */
export const JACKPOT_COIN_IDS: Readonly<Record<CoinJackpotTier, string>> = { mini: "MINI", minor: "MINOR", major: "MAJOR" };

/** A cell's symbol, parsed. */
export type CellSymbol =
  | { kind: "blank" }
  | { kind: "coin"; valueMinor: string }
  | { kind: "jackpot"; tier: CoinJackpotTier }
  /** A mystery coin, or a mystery jackpot (`jackpot`: it reveals a jackpot coin only). */
  | { kind: "mystery"; jackpot: boolean }
  | { kind: "collect"; valueMinor: string | null };

const MINOR = /^\d+$/;

/** The symbol an id names, or null for an id this game does not have. */
export function parseSymbolId(id: string): CellSymbol | null {
  if (id === BLANK) return { kind: "blank" };
  if (id === MYSTERY) return { kind: "mystery", jackpot: false };
  if (id === MYSTERY_JACKPOT) return { kind: "mystery", jackpot: true };
  if (id === COLLECT) return { kind: "collect", valueMinor: null };
  if (id === "MINI") return { kind: "jackpot", tier: "mini" };
  if (id === "MINOR") return { kind: "jackpot", tier: "minor" };
  if (id === "MAJOR") return { kind: "jackpot", tier: "major" };
  const [prefix, value, extra] = id.split(":");
  if (extra !== undefined || value === undefined || !MINOR.test(value)) return null;
  if (prefix === "C") return { kind: "coin", valueMinor: value };
  if (prefix === COLLECT) return { kind: "collect", valueMinor: value };
  return null;
}

export function coinId(valueMinor: bigint | string): string {
  return `C:${valueMinor.toString()}`;
}

export function collectId(valueMinor: bigint | string): string {
  return `${COLLECT}:${valueMinor.toString()}`;
}

/** A mystery of either kind: a mystery coin or a mystery jackpot, revealed at the end of the bonus. */
export function isMysteryId(id: string): boolean {
  return id === MYSTERY || id === MYSTERY_JACKPOT;
}

/** A bonus symbol is anything but a blank: it covers a line and is held in the bonus. */
export function isBonusSymbolId(id: string): boolean {
  return id !== BLANK;
}

/** A jackpot's amount at `betMinor`, whole minor units. */
export function jackpotAmountMinor(tier: JackpotTier, betMinor: bigint): bigint {
  return betMinor * BigInt(JACKPOT_MULTIPLIERS[tier]);
}

/**
 * What a cell is worth on the board at `betMinor` before its multiplier: a coin's or a collect's value, a jackpot coin's
 * jackpot; a blank, a mystery and an empty collect are worth nothing yet.
 */
export function cellValueMinor(id: string, betMinor: bigint): bigint {
  const symbol = parseSymbolId(id);
  if (!symbol) throw new Error(`cellValueMinor: unknown symbol "${id}"`);
  switch (symbol.kind) {
    case "coin":
      return BigInt(symbol.valueMinor);
    case "collect":
      return symbol.valueMinor === null ? 0n : BigInt(symbol.valueMinor);
    case "jackpot":
      return jackpotAmountMinor(symbol.tier, betMinor);
    case "blank":
    case "mystery":
      return 0n;
  }
}

/** The three pots, in their order over the grid: LIFE (green), MULTI (red), GROW (blue). */
export const POTS = ["life", "multi", "grow"] as const;
export type PotKind = (typeof POTS)[number];

/** A pot holds up to this many gems; the last one fills it and starts the bonus. */
export const POT_CAPACITY = 3;

export type PotLevels = Readonly<Record<PotKind, number>>;

export const EMPTY_POTS: PotLevels = { life: 0, multi: 0, grow: 0 };
